import moment from "moment";
import * as React from "react";

import {
    ACTIVITY_SESSIONS_V2_QUERY,
    shiftDateForDemoRange,
    shiftDateRangeToDemoDateRange,
    type ActivityKind,
    type ActivitySessionsV2Query,
    type ActivitySessionsV2QueryVariables,
} from "@khan/activity-sessions-query";
import {type DateRange} from "@khan/component-date-range-dropdown";
import type {AsyncApolloQueryResult} from "@khan/data-gql-apollo";
import Log, {Errors} from "@khan/logging";

import ApolloQuery from "#data-access/gql/apollo-query.tsx";
import {
    withFeatureIsOn,
    type WithFeatureIsOn,
    type WithoutFeatureIsOn,
} from "#data-access/with-feature-is-on.tsx";
import {
    DATE_FORMAT,
    momentToNonDisplayString,
    type DateRange as DateRangeMoment,
} from "#reports/util.ts";

import {ACTIVITY_SESSIONS_QUERY} from "./queries.ts";
import type {
    ActivitySessionsQuery,
    ActivitySessionsQueryVariables,
    ActivitySessionsReportRow,
    ActivityTime,
    CourseType,
} from "./types.ts";

export type ActivitySessionsQueryResult = {
    activityTime: ActivityTime;
    sessions: Array<ActivitySessionsReportRow>;
    isLoading: boolean;
    nextCursor: string | null | undefined;
    fetchPage: (pageCursor?: string | null | undefined) => void;
};

type QueryResult = AsyncApolloQueryResult<
    ActivitySessionsQuery,
    ActivitySessionsQueryVariables
>;
type QueryResultV2 = AsyncApolloQueryResult<
    ActivitySessionsV2Query,
    ActivitySessionsV2QueryVariables
>;

type Props = WithFeatureIsOn & {
    studentKaid: string;
    dateRange: DateRange | null | undefined;
    courseFilter: CourseType | null | undefined;
    activityFilter: ActivityKind | null | undefined;
    children: (result: ActivitySessionsQueryResult) => React.ReactNode;
};

type State = {
    currentCursor: string | null | undefined;
};

class ActivitySessionsReportQuery extends React.Component<Props, State> {
    state: State = {
        currentCursor: null,
    };

    mapDataToResult: (arg1: QueryResult) => ActivitySessionsQueryResult = ({
        // NOTE(kevinb): Do we need this default value here?
        // @ts-expect-error - TS2741 - Property 'user' is missing in type '{}' but required in type '{ user: ActivitySessionsQuery_user | null | undefined; }'.
        data = {},

        loading: isLoading,
    }: QueryResult): ActivitySessionsQueryResult => {
        const {user} = data;
        const {featureIsOn: flag} = this.props;
        const activityTime = user?.activityLog?.time ?? {
            __typename: "ActivityTime",
            exerciseMinutes: 0,
            totalMinutes: 0,
        };
        const activitySessions = user?.activityLog?.activitySessions;
        const nextCursor = activitySessions?.pageInfo?.nextCursor ?? null;
        const maybeSessions = activitySessions?.sessions ?? [];
        const sessions: Array<ActivitySessionsReportRow> = [];
        for (const session of maybeSessions) {
            if (session) {
                if (!isValidActivityKind(session.activityKind)) {
                    Log.error(
                        `Invalid ActivityKind: ${JSON.stringify(
                            session.activityKind,
                        )}`,
                        Errors.Internal,
                    );
                    break;
                }
                // Session is not null, and its activityKind is in one of two
                // valid states:
                // 1. null or undefined, or
                // 2. activityKind.id matches one of the union ActivityKind
                if (flag && session.eventTimestamp) {
                    session.eventTimestamp = shiftDateForDemoRange(
                        moment(session.eventTimestamp),
                        false,
                        true,
                    )?.toISOString();
                }
                sessions.push(session as ActivitySessionsReportRow);
            }
        }
        return {
            activityTime,
            sessions,
            isLoading,
            nextCursor,
            fetchPage: (pageCursor?: string | null) => {
                this.setState({currentCursor: pageCursor});
            },
        };
    };

    mapDataToResultV2: (arg1: QueryResultV2) => ActivitySessionsQueryResult = ({
        // NOTE(kevinb): Do we need this default value here?
        // @ts-expect-error - TS2741 - Property 'user' is missing in type '{}' but required in type '{ user: ActivitySessionsQuery_user | null | undefined; }'.
        data = {},

        loading: isLoading,
    }: QueryResultV2): ActivitySessionsQueryResult => {
        const {user} = data;
        const {featureIsOn: flag} = this.props;
        const activityTime = user?.activityLogV2?.time ?? {
            __typename: "ActivityTime",
            exerciseMinutes: 0,
            totalMinutes: 0,
        };
        const activitySessions = user?.activityLogV2?.activitySessions;
        const nextCursor = activitySessions?.pageInfo?.nextCursor ?? null;
        const maybeSessions = activitySessions?.sessions ?? [];
        const sessions: Array<ActivitySessionsReportRow> = [];
        for (const session of maybeSessions) {
            if (session) {
                // Session is not null, and its activityKind is in one of two
                // valid states:
                // 1. null or undefined, or
                // 2. activityKind.id matches one of the union ActivityKind
                if (flag && session.eventTimestamp) {
                    session.eventTimestamp = shiftDateForDemoRange(
                        moment(session.eventTimestamp),
                        false,
                        true,
                    )?.toISOString();
                }
                sessions.push(session as ActivitySessionsReportRow);
            }
        }
        return {
            activityTime,
            sessions,
            isLoading,
            nextCursor,
            fetchPage: (pageCursor?: string | null) => {
                this.setState({currentCursor: pageCursor});
            },
        };
    };

    render(): React.ReactNode {
        const {children} = this.props;
        const variables = mapPropsToOptions(this.props);
        const {currentCursor: after} = this.state;

        const start = variables.startDate
            ? new Date(variables.startDate)
            : null;
        const end = variables.endDate ? new Date(variables.endDate) : null;
        const shouldUseActivitySessionsV2 =
            (start && start.getTime() >= new Date("2023-10-26").getTime()) ||
            (end && end.getTime() > new Date("2023-12-05").getTime());

        return shouldUseActivitySessionsV2 ? (
            <ApolloQuery
                key={String(start) + String(end)}
                query={ACTIVITY_SESSIONS_V2_QUERY}
                variables={{...variables, after}}
                fetchPolicy="cache-and-network"
            >
                {(result) => children(this.mapDataToResultV2(result))}
            </ApolloQuery>
        ) : (
            <ApolloQuery
                key={String(start) + String(end)}
                query={ACTIVITY_SESSIONS_QUERY}
                variables={{...variables, after}}
                fetchPolicy="cache-and-network"
            >
                {(result) => children(this.mapDataToResult(result))}
            </ApolloQuery>
        );
    }
}

const mapPropsToOptions = (props: Props) => {
    const {
        studentKaid,
        dateRange,
        courseFilter: courseType,
        activityFilter: activityKind,
        featureIsOn: flag,
    } = props;
    let range: DateRangeMoment | null | undefined = {
        startDate: dateRange?.startDate
            ? moment(dateRange.startDate).startOf("day")
            : moment().startOf("day"),
        endDate: dateRange?.endDate
            ? moment(dateRange.endDate).startOf("day")
            : moment().startOf("day"),
    };
    if (flag && range) {
        range = shiftDateRangeToDemoDateRange(range);
    }

    return {
        studentKaid,
        startDate: momentToNonDisplayString(range?.startDate, DATE_FORMAT),
        endDate: momentToNonDisplayString(range?.endDate, DATE_FORMAT),
        courseType,
        activityKind,
        pageSize: 10,
    };
};

const isValidActivityKind = (
    activityKind?: {
        id: string | null | undefined;
    } | null,
): boolean => {
    return (
        !activityKind ||
        activityKind.id === "Article" ||
        activityKind.id === "Challenge" ||
        activityKind.id === "CourseChallenge" ||
        activityKind.id === "MasteryChallenge" ||
        activityKind.id === "Exercise" ||
        activityKind.id === "Interactive" ||
        activityKind.id === "Project" ||
        activityKind.id === "Talkthrough" ||
        activityKind.id === "UserProgram" ||
        activityKind.id === "TopicQuiz" ||
        activityKind.id === "TopicUnitTest" ||
        activityKind.id === "Video" ||
        activityKind.id === "AIGuideActivities" ||
        activityKind.id === "AiGuideWriting" ||
        // These are deprecated Test Prep activities that used to be generated by GTP and OSP.
        // Since those products have been shut down, no new messages with these kinds should be
        // generated.
        activityKind.id === "LSAT" ||
        activityKind.id === "PraxisMath" ||
        activityKind.id === "PraxisReading" ||
        activityKind.id === "PraxisWriting" ||
        activityKind.id === "SAT"
    );
};

type ExportProps = WithoutFeatureIsOn<
    JSX.LibraryManagedAttributes<
        typeof ActivitySessionsReportQuery,
        React.ComponentProps<typeof ActivitySessionsReportQuery>
    >
>;

export default withFeatureIsOn("demo_date_shift")(
    ActivitySessionsReportQuery,
) as React.ComponentType<ExportProps>;
